import transformDeclPairs from 'css-to-react-native';
import {
  ExecutionContext,
  IInlineStyle,
  IInlineStyleConstructor,
  RuleSet,
  StyleSheet,
} from '../types';
import flatten from '../utils/flatten';
import { joinStringArray } from '../utils/joinStrings';

// List of CSS values not supported by React Native
export const RN_UNSUPPORTED_VALUES = ['fit-content', 'min-content', 'max-content'];

/**
 * True when the nearest preceding non-whitespace character before index `i`
 * is `:`, meaning a `//` starting at `i` is a value that just started (a
 * protocol-relative URL, e.g. `--cdn: //cdn.example.com/x.png`) rather than
 * a JS-style line comment.
 */
function isPrecededByColon(css: string, i: number): boolean {
  let j = i - 1;
  while (j >= 0) {
    const code = css.charCodeAt(j);
    // space, tab, newline, carriage return, form feed
    if (code === 32 || code === 9 || code === 10 || code === 13 || code === 12) {
      j--;
      continue;
    }
    return code === 58; // :
  }
  return false;
}

/** True when the `(` at index `i` opens a `url(` function, in any letter case. */
function isUrlOpen(css: string, i: number): boolean {
  return (
    i >= 3 &&
    (css.charCodeAt(i - 3) | 32) === 117 &&
    (css.charCodeAt(i - 2) | 32) === 114 &&
    (css.charCodeAt(i - 1) | 32) === 108
  );
}

/**
 * Strips `/* *\/` block comments. Builds the output by copying whole runs
 * between comment boundaries (`substring`), not character by character, and
 * returns the original string unchanged when no comment was actually
 * removed (e.g. a `/*` that only ever appears inside a quoted string).
 */
function stripComments(css: string): string {
  if (css.indexOf('/*') === -1) return css;
  let result = '';
  let start = 0;
  let i = 0;
  let quote = 0; // 0 = none, 34 = ", 39 = '
  let sawComment = false;
  const len = css.length;
  while (i < len) {
    const ch = css.charCodeAt(i);
    if (quote) {
      if (ch === 92) {
        // backslash: skip the escape sequence without ending the quote
        i += 2;
        continue;
      }
      if (ch === quote) quote = 0;
      i++;
    } else if (ch === 34 || ch === 39) {
      quote = ch;
      i++;
    } else if (ch === 47 && css.charCodeAt(i + 1) === 42) {
      // /* comment */
      result += css.substring(start, i);
      sawComment = true;
      const end = css.indexOf('*/', i + 2);
      if (end === -1) {
        start = len;
        i = len;
        break;
      }
      i = end + 2;
      start = i;
    } else {
      i++;
    }
  }
  if (!sawComment) return css;
  result += css.substring(start, len);
  return result;
}

/**
 * Strips both `/* *\/` block comments and `//` line comments, with
 * paren-depth tracking so a bare url() argument (e.g. `url(http://...)`)
 * is never mistaken for a line comment. Only reached when the input
 * actually contains `//`; a `/* *\/`-only input uses the cheaper
 * `stripComments` above instead, which needs no paren tracking.
 *
 * Builds the output by copying whole runs between comment boundaries
 * (`substring`), not character by character, and returns the original
 * string unchanged when no comment was actually removed (e.g. a bare `//`
 * that only ever appears inside a quoted string, a url(), or right after a
 * colon).
 */
function stripAllComments(css: string): string {
  let result = '';
  let start = 0;
  let i = 0;
  let quote = 0; // 0 = none, 34 = ", 39 = '
  let urlDepth = 0;
  let sawComment = false;
  const len = css.length;
  while (i < len) {
    const ch = css.charCodeAt(i);
    if (quote) {
      if (ch === 92) {
        // backslash: skip the escape sequence without ending the quote
        i += 2;
        continue;
      }
      if (ch === quote) quote = 0;
      i++;
    } else if (ch === 34 || ch === 39) {
      quote = ch;
      i++;
    } else if (ch === 47 && css.charCodeAt(i + 1) === 42) {
      // /* comment */. Checked before the paren-depth branches below so a
      // block comment inside a function call (e.g. `calc(1px /* a */ + 2px)`)
      // is stripped the same way it is outside one.
      result += css.substring(start, i);
      sawComment = true;
      const end = css.indexOf('*/', i + 2);
      if (end === -1) {
        start = len;
        i = len;
        break;
      }
      i = end + 2;
      start = i;
    } else if (urlDepth > 0) {
      // Inside url(...), leave everything untouched so url(//cdn/...) and
      // url(http://...) survive. Other function calls (calc, rgba) are not
      // shielded, so a line comment inside their arguments is stripped.
      if (ch === 40) urlDepth++;
      else if (ch === 41) urlDepth--;
      i++;
    } else if (ch === 40 && isUrlOpen(css, i)) {
      urlDepth = 1;
      i++;
    } else if (ch === 47 && css.charCodeAt(i + 1) === 47 && !isPrecededByColon(css, i)) {
      // JS-style // line comment, through the next newline (or end of input).
      // Skipped when the nearest preceding non-whitespace character is `:`
      // so a bare, unquoted URL scheme or protocol-relative value outside of
      // url()/quotes (e.g. `--api-url: https://host/path` or
      // `--cdn: //cdn.example.com/x.png`) is left alone rather than treated
      // as a comment.
      result += css.substring(start, i);
      sawComment = true;
      const nl = css.indexOf('\n', i);
      if (nl === -1) {
        start = len;
        i = len;
        break;
      }
      i = nl;
      start = i;
    } else {
      i++;
    }
  }
  if (!sawComment) return css;
  result += css.substring(start, len);
  return result;
}

/**
 * Extract CSS declaration pairs from flat CSS text.
 * Only handles `property: value;` - selectors, at-rules, and nesting
 * are not supported (and not expected in the native inline style path).
 */
export function parseCSSDeclarations(rawCss: string): [string, string][] {
  const css = rawCss.indexOf('//') !== -1 ? stripAllComments(rawCss) : stripComments(rawCss);
  const pairs: [string, string][] = [];
  const len = css.length;
  let i = 0;

  while (i < len) {
    // skip whitespace, stray semicolons, and brace-delimited blocks (selectors/at-rules leaking through)
    while (i < len) {
      const c = css[i];
      if (c === ' ' || c === '\n' || c === '\r' || c === '\t' || c === ';' || c === '}') {
        i++;
      } else if (c === '{') {
        // skip entire block (handles `.foo { color: red; font-size: 12px; }`)
        let depth = 1;
        i++;
        while (i < len && depth > 0) {
          if (css[i] === '{') depth++;
          else if (css[i] === '}') depth--;
          i++;
        }
      } else {
        break;
      }
    }

    if (i >= len) break;

    // find the colon separating property from value
    const colonIdx = css.indexOf(':', i);
    if (colonIdx === -1) break;

    const prop = css.substring(i, colonIdx).trim();

    let parenDepth = 0;
    let quote = 0; // 0 = none, 34 = ", 39 = '
    let j = colonIdx + 1;
    while (j < len) {
      const ch = css.charCodeAt(j);
      if (quote) {
        if (ch === 92) {
          j++; // skip escaped character
        } else if (ch === quote) {
          quote = 0;
        }
      } else if (ch === 34 || ch === 39) {
        quote = ch;
      } else if (ch === 40) {
        parenDepth++;
      } else if (ch === 41) {
        if (parenDepth > 0) parenDepth--;
      } else if (ch === 59 && parenDepth === 0) {
        break;
      }
      j++;
    }

    // Unclosed quote or paren: drop this declaration, find first ; to resume from
    if (j >= len && (quote !== 0 || parenDepth > 0)) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn(
          `[styled-components/native] Invalid CSS in declaration "${prop}": ` +
            (quote ? 'unterminated string' : 'unclosed parenthesis') +
            '. This declaration was dropped.'
        );
      }
      const semi = css.indexOf(';', colonIdx + 1);
      i = semi !== -1 ? semi + 1 : len;
      continue;
    }

    const value = css.substring(colonIdx + 1, j).trim();

    if (prop && value) {
      // Selector or at-rule detected: warn and skip entire block (including nested declarations)
      if (prop[0] === '@' || prop.indexOf('{') !== -1) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn(
            `[styled-components/native] "${prop}" is not supported as an inline style and will be ignored. ` +
              'Only CSS declarations (property: value) are supported in React Native.'
          );
        }
        // Skip to matching closing brace - count braces in both prop and value
        let depth = 0;
        let k = j;
        for (let vi = i; vi < j; vi++) {
          if (css[vi] === '{') depth++;
          else if (css[vi] === '}') depth--;
        }
        // Scan forward for remaining unmatched braces
        while (k < len && depth > 0) {
          if (css[k] === '{') depth++;
          else if (css[k] === '}') depth--;
          k++;
        }
        i = k;
        continue;
      }
      pairs.push([prop, value]);
    }

    i = j + 1; // skip past the semicolon
  }

  return pairs;
}

let generated = new Map<string, any>();

/** Clear the cached CSS-to-style-object mappings. Useful in tests or long-running RN apps with highly dynamic styles. */
export const resetStyleCache = (): void => {
  generated = new Map();
};

/**
 * Parse flat CSS into a style object via css-to-react-native, with caching.
 * Keyed by the flat CSS string itself: the transform is a pure function of it,
 * and a string key cannot collide the way a 32-bit hash could.
 */
export function cssToStyleObject(flatCSS: string, styleSheet: StyleSheet) {
  const cached = generated.get(flatCSS);
  if (cached !== undefined) return cached;

  const declPairs: [string, string][] = [];
  for (const [prop, value] of parseCSSDeclarations(flatCSS)) {
    if (RN_UNSUPPORTED_VALUES.includes(value)) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn(
          `[styled-components/native] The value "${value}" for property "${prop}" is not supported in React Native and will be ignored.`
        );
      }
    } else {
      declPairs.push([prop, value]);
    }
  }

  // RN does not support differing border values for Image components (but does for View).
  // https://github.com/styled-components/styled-components/issues/4181
  const styleObject = transformDeclPairs(declPairs, ['borderWidth', 'borderColor']);

  const result = styleSheet.create({ generated: styleObject }).generated;
  generated.set(flatCSS, result);
  return result;
}

/**
 * InlineStyle takes arbitrary CSS and generates a flat object
 */
export default function makeInlineStyleClass<Props extends object>(styleSheet: StyleSheet) {
  const InlineStyle: IInlineStyleConstructor<Props> = class InlineStyle implements IInlineStyle<Props> {
    rules: RuleSet<Props>;

    constructor(rules: RuleSet<Props>) {
      this.rules = rules;
    }

    generateStyleObject(executionContext: ExecutionContext & Props) {
      const flatCSS = joinStringArray(
        flatten(this.rules as RuleSet<object>, executionContext) as string[]
      );
      return cssToStyleObject(flatCSS, styleSheet);
    }
  };

  return InlineStyle;
}
